import { useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { Radar, TrendingUp, Zap, Lock, CheckCircle2, Sparkles } from 'lucide-react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import usePlatform from '@/hooks/use-platform';
import { cn } from '@/lib/utils';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import CustomContent from './CustomContent';

const features = [
  {
    Icon: Radar,
    title: 'Anomaly detection on autopilot',
    description:
      'Sentinel catches campaign drops, tracking gaps, and budget anomalies before they cost you.',
  },
  {
    Icon: TrendingUp,
    title: 'Every channel, one source of truth',
    description: 'GA4, Google Ads, Meta, TikTok, GSC, Shopify — unified in minutes, not weeks.',
  },
  {
    Icon: Zap,
    title: "Fix issues, don't find them",
    description: 'Every alert ships with root-cause hypotheses ranked by likelihood.',
  },
];

const AppLayout = () => {
  const { experienceSettings, theme } = useContext(PageContext);
  const { isMobile } = usePlatform();
  const logoUrl =
    experienceSettings &&
    getBrandingLogoUrl({
      theme,
      branding: experienceSettings.branding,
      isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
    });

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Ambient decorative blobs — inline styles so they don't depend on Tailwind JIT */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-72 -left-56 h-[680px] w-[680px] rounded-full blur-3xl"
          style={{ background: 'rgba(215, 11, 61, 0.12)' }}
        />
        <div
          className="absolute top-1/3 -right-72 h-[820px] w-[820px] rounded-full blur-3xl"
          style={{ background: 'rgba(0, 89, 254, 0.05)' }}
        />
        <div
          className="absolute -bottom-56 left-1/3 h-[520px] w-[520px] rounded-full blur-3xl"
          style={{ background: 'rgba(215, 11, 61, 0.08)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(71, 85, 105, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(71, 85, 105, 0.07) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center px-6 pt-6 sm:px-10 sm:pt-8">
        {logoUrl && (
          <img src={logoUrl} alt="App logo" className="block h-9 w-auto object-contain sm:h-10" />
        )}
      </header>

      {/* Main */}
      <main className="relative z-10 flex min-h-[calc(100vh-5rem)] items-center justify-center px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
        <div
          className={cn(
            'grid w-full max-w-[1180px] grid-cols-1 overflow-hidden rounded-2xl border border-border/60 bg-card/55 shadow-xl shadow-primary/10 ring-1 ring-black/[0.02] backdrop-blur-3xl backdrop-saturate-150',
            'lg:grid-cols-[1.05fr_1fr] dark:ring-white/[0.04]'
          )}
        >
          {/* Left: marketing / brand storytelling */}
          <aside
            className="relative hidden flex-col justify-between overflow-hidden border-r border-border/60 p-10 lg:flex"
            style={{
              background:
                'linear-gradient(to bottom right, rgba(255,255,255,0.3), rgba(255,255,255,0.4), rgba(215, 11, 61, 0.06))',
            }}
          >
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl"
                style={{ background: 'rgba(215, 11, 61, 0.10)' }}
              />
              <div
                className="absolute bottom-0 right-0 h-80 w-80 rounded-full blur-3xl"
                style={{ background: 'rgba(0, 89, 254, 0.08)' }}
              />
            </div>

            <div className="relative flex flex-col gap-8">
              <span
                className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground backdrop-blur-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(222, 222, 222, 0.6)',
                }}
              >
                <span className="inline-block size-1.5 rounded-full bg-primary" />
                Welcome back
              </span>

              <div>
                <h1 className="text-[42px] font-semibold leading-[1.05] tracking-tight text-card-foreground">
                  Marketing intelligence
                  <span className="block text-primary">that watches your back.</span>
                </h1>
                <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                  Trackian unifies every channel and surfaces what changed — so your team spends time fixing problems, not finding them.
                </p>
              </div>

              <ul className="flex flex-col gap-4">
                {features.map(({ Icon, title, description }) => (
                  <li key={title} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                      <Icon className="size-4" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-card-foreground">{title}</p>
                      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Right: outlet (SignIn / Register / etc.) */}
          <section className="relative flex flex-col justify-center px-6 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
            <div className="mx-auto flex w-full max-w-[400px] flex-col">
              {!isMobile && <CustomContent className={layoutClassNames.customContent} />}
              <Outlet />
            </div>
          </section>
        </div>
      </main>

      {/* Trust strip */}
      <footer className="relative z-10 px-6 pb-8 sm:px-10">
        <ul className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
          <li className="inline-flex items-center gap-1.5">
            <Lock className="size-3.5 text-primary/70" />
            GDPR compliant
          </li>
          <li className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="size-3.5 text-primary/70" />
            Cancel anytime
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-primary/70" />
            Trusted by 100+ agencies
          </li>
        </ul>
      </footer>
    </div>
  );
};

export default AppLayout;
